/**
 * BrewNectar Choose Your Plan — gallery, plan selection, Shopify cart add
 * Used by homepage choose-plan section and product main section.
 */
(function () {
  const SECTION_SELECTOR = '[data-bn-cyp-section], [data-bn-pd-section]';

  function getProduct(section) {
    let el = section.querySelector('[data-bn-cyp-product-json]');
    if (!el?.textContent) {
      const root = section.closest('[id^="shopify-section-"]') || section.parentElement;
      el = root?.querySelector('[data-bn-cyp-product-json]');
    }
    if (!el?.textContent) return null;
    try {
      const parsed = JSON.parse(el.textContent);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      if (!Array.isArray(parsed.variants)) parsed.variants = [];
      return parsed;
    } catch {
      return null;
    }
  }

  function getSelectedOptions(section) {
    return Array.from(section.querySelectorAll('[data-bn-pd-option]')).map((select) => select.value);
  }

  function findVariant(product, options) {
    if (!product?.variants?.length || !options.length) return null;
    return (
      product.variants.find((variant) =>
        variant.options.every((value, index) => value === options[index])
      ) || null
    );
  }

  function formatMoney(cents) {
    const format =
      (typeof window !== 'undefined' && window.Shopify && window.Shopify.money_format) ||
      '${{amount}}';
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return `$${amount}`;
  }

  function getPlanButtonPrice(button) {
    if (!button) return '';
    const sticky = button.dataset.stickyPerMonth;
    if (sticky) return sticky.trim();
    const amountEl = button.querySelector('.bn-cyp__plan-price-amount');
    return amountEl ? amountEl.textContent.trim() : '';
  }

  function buildCtaLabel(template, priceDisplay) {
    const price = (priceDisplay || '').trim();
    if (!template) return price;
    if (!price) return template;
    if (/\{price\}/i.test(template)) {
      return template.replace(/\{price\}/gi, price);
    }
    if (/\$[\d,.]+/.test(template)) {
      return template.replace(/\$[\d,.]+/, price);
    }
    return template;
  }

  function updateCtaLabel(section, button, mode) {
    const ctaLabel = section.querySelector('[data-bn-cyp-cta-label]');
    if (!ctaLabel) return;

    if (mode === 'one_time') {
      const oneTimeBtn = button || section.querySelector('[data-bn-cyp-one-time]');
      const template = oneTimeBtn?.dataset.ctaOnetime || ctaLabel.dataset.defaultOnetime || '';
      const price = getPlanButtonPrice(oneTimeBtn);
      ctaLabel.textContent = buildCtaLabel(template, price);
      return;
    }

    const planBtn = button || section.querySelector('[data-bn-cyp-plan].is-selected');
    const template = planBtn?.dataset.ctaSubscription || ctaLabel.dataset.defaultSubscription || '';
    const price = getPlanButtonPrice(planBtn);
    ctaLabel.textContent = buildCtaLabel(template, price);
  }

  function updateVariantUrl(section, variantId) {
    if (!variantId || section.dataset.bnCypUpdateUrl !== 'true') return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  function syncFormInputs(variantInput, sellingPlanInput) {
    [variantInput, sellingPlanInput].forEach((input) => {
      if (!input || input.disabled) return;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  function getSellingPlansFromProduct(product) {
    const plans = [];
    (product?.selling_plan_groups || []).forEach((group) => {
      (group.selling_plans || []).forEach((plan) => {
        plans.push(plan);
      });
    });
    return plans;
  }

  function getSubscriptionMonthsFromButton(button) {
    const months = parseInt(button.dataset.subscriptionMonths, 10);
    if (months > 0) return months;

    const planKey = button.dataset.bnCypPlan || '';
    if (planKey === 'subscribe-1') return 1;
    if (planKey === 'subscribe-2') return 2;
    if (planKey === 'subscribe-3') return 3;

    const supplyDays = parseInt(button.dataset.supplyDays, 10);
    if (supplyDays === 30) return 1;
    if (supplyDays === 60) return 2;
    if (supplyDays === 90) return 3;

    return 0;
  }

  function planNameMatchesMonths(planName, months) {
    const name = planName.toLowerCase();
    const patterns = {
      1: ['1 month', '1-month', '1 months', 'every 1 month', '1 mo'],
      2: ['2 month', '2-month', '2 months', 'every 2 month', '2 mo'],
      3: ['3 month', '3-month', '3 months', 'every 3 month', '3 mo', 'quarter'],
    };
    return (patterns[months] || []).some((kw) => name.includes(kw));
  }

  function resolveSellingPlanForButton(button, product) {
    if (!button || !product) return null;

    const plans = getSellingPlansFromProduct(product);
    if (!plans.length) return null;

    const explicitId = button.dataset.sellingPlanId;
    if (explicitId) {
      const byId = plans.find((p) => String(p.id) === String(explicitId));
      if (byId) return byId;
    }

    const nameMatch = (button.dataset.sellingPlanNameMatch || '').trim().toLowerCase();
    if (nameMatch) {
      const byName = plans.find((p) => p.name.toLowerCase().includes(nameMatch));
      if (byName) return byName;
    }

    const subscriptionMonths = getSubscriptionMonthsFromButton(button);
    if (subscriptionMonths > 0) {
      const byMonths = plans.find((p) => planNameMatchesMonths(p.name, subscriptionMonths));
      if (byMonths) return byMonths;
    }

    const position = parseInt(button.dataset.sellingPlanPosition, 10);
    if (position > 0 && plans[position - 1]) return plans[position - 1];

    return null;
  }

  function getAllocationForPlan(variant, sellingPlanId) {
    if (!variant?.selling_plan_allocations?.length || !sellingPlanId) return null;
    return variant.selling_plan_allocations.find((allocation) => {
      const id = allocation.selling_plan_id || allocation.selling_plan?.id;
      return String(id) === String(sellingPlanId);
    });
  }

  function isPlanAvailableForVariant(variant, sellingPlanId) {
    if (!variant?.available || !sellingPlanId) return false;

    const allocations = variant.selling_plan_allocations;
    if (!allocations || allocations.length === 0) {
      // Product JSON on non-product templates often omits allocations — trust Liquid output.
      return true;
    }

    return Boolean(getAllocationForPlan(variant, sellingPlanId));
  }

  function hydratePlanButton(button, product) {
    if (!button || !product) return;

    const resolvedPlan = resolveSellingPlanForButton(button, product);
    if (!resolvedPlan) return;

    button.dataset.sellingPlanId = String(resolvedPlan.id);

    const variantId = button.dataset.variantId;
    const variant = product.variants?.find((v) => String(v.id) === String(variantId));
    const available = isPlanAvailableForVariant(variant, resolvedPlan.id);
    button.dataset.available = available ? 'true' : 'false';
    button.disabled = !available;

    if (variant) updatePlanButtonStickyData(button, variant);
  }

  function setSellingPlanOnForm(section, sellingPlanId, isOneTime, syncingRef) {
    const form = section.querySelector('.bn-cyp__form');
    if (!form) return;

    if (syncingRef) syncingRef.value = true;

    const primaryInput = form.querySelector('[data-bn-cyp-selling-plan]');
    form.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
      if (input !== primaryInput) input.removeAttribute('name');
    });

    const inputs = primaryInput ? [primaryInput] : form.querySelectorAll('input[name="selling_plan"]');
    inputs.forEach((input) => {
      if (isOneTime || !sellingPlanId) {
        input.value = '';
        input.setAttribute('disabled', 'disabled');
      } else {
        input.value = sellingPlanId;
        input.removeAttribute('disabled');
      }
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    if (!isOneTime && sellingPlanId) {
      const widgetInput = form.querySelector(
        `[data-bn-pd-subscription-app] input[type="radio"][value="${sellingPlanId}"], ` +
          `[data-bn-pd-subscription-app] input[type="radio"][name="selling_plan"][value="${sellingPlanId}"]`
      );
      if (widgetInput && !widgetInput.checked) {
        widgetInput.checked = true;
        widgetInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (syncingRef) {
      requestAnimationFrame(() => {
        syncingRef.value = false;
      });
    }
  }

  function readStickyDataFromButton(button) {
    if (!button) {
      return {
        perMonth: '',
        compareAt: '',
        discount: '',
        planLabel: '',
      };
    }

    let perMonth = button.dataset.stickyPerMonth || '';
    let compareAt = button.dataset.stickyCompareAt || '';
    let discount = button.dataset.stickyDiscount || '';
    let planLabel = button.dataset.stickyLabel || '';

    if (!perMonth) {
      const amountEl = button.querySelector('.bn-cyp__plan-price-amount');
      if (amountEl) perMonth = amountEl.textContent.trim();
    }

    if (!planLabel) {
      const titleEl = button.querySelector('.bn-cyp__plan-title');
      if (titleEl) planLabel = titleEl.textContent.trim();
    }

    return { perMonth, compareAt, discount, planLabel };
  }

  function updatePlanButtonStickyData(btn, variant) {
    if (!btn || !variant) return;

    const sellingPlanId = btn.dataset.sellingPlanId;
    if (!sellingPlanId) return;

    const allocations = variant.selling_plan_allocations || [];
    const allocation = allocations.find((a) => {
      const id = a.selling_plan_id || a.selling_plan?.id;
      return String(id) === String(sellingPlanId);
    });

    if (!allocation) return;

    const perDelivery =
      allocation.per_delivery_price != null ? allocation.per_delivery_price : allocation.price;
    const priceDisplay = formatMoney(perDelivery);
    btn.dataset.stickyPerMonth = priceDisplay;

    const amountEl = btn.querySelector('.bn-cyp__plan-price-amount');
    if (amountEl) amountEl.textContent = priceDisplay;

    const dayEl = btn.querySelector('.bn-cyp__plan-price-day');
    if (dayEl && perDelivery > 0) {
      // Manus-style: always ÷ 30 (monthly rate), never ÷ 60/90 supply days
      let monthBasis = perDelivery;
      const supplyDays = parseInt(btn.dataset.supplyDays, 10) || 30;
      // If only total prepaid price is available, convert to monthly first
      if (allocation.per_delivery_price == null && supplyDays > 30) {
        const months = Math.max(1, Math.round(supplyDays / 30));
        monthBasis = Math.round(perDelivery / months);
      }
      const dayCents = Math.round(monthBasis / 30);
      dayEl.textContent = `${formatMoney(dayCents)} USD / DAY`;
    }

    const compareAt = allocation.compare_at_price || 0;
    const price = allocation.price || 0;

    if (compareAt > price) {
      btn.dataset.stickyCompareAt = formatMoney(compareAt);
      const savePct = Math.round(((compareAt - price) * 100) / compareAt);
      btn.dataset.stickyDiscount = `${savePct}% OFF`;
    } else {
      btn.dataset.stickyCompareAt = '';
      btn.dataset.stickyDiscount = '';
    }
  }

  function getPlanChangeDetail(section) {
    const oneTimeBtn = section.querySelector('[data-bn-cyp-one-time]');
    const variantInput = section.querySelector('[data-bn-cyp-variant-id]');
    const sellingPlanInput = section.querySelector('[data-bn-cyp-selling-plan]');
    const cta = section.querySelector('[data-bn-cyp-cta]');
    const quantityInput = section.querySelector('.bn-cyp__form [name="quantity"]');
    const product = getProduct(section);

    let mode = 'subscription';
    let button = section.querySelector('[data-bn-cyp-plan].is-selected');

    if (oneTimeBtn?.classList.contains('is-selected')) {
      mode = 'one_time';
      button = oneTimeBtn;
    } else if (
      (!button || !sellingPlanInput?.value || sellingPlanInput.disabled) &&
      oneTimeBtn &&
      !section.querySelector('[data-bn-cyp-plan].is-selected')
    ) {
      mode = 'one_time';
      button = oneTimeBtn;
    }

    const sellingPlanId =
      sellingPlanInput && !sellingPlanInput.disabled ? sellingPlanInput.value || '' : '';

    if (sellingPlanId && mode !== 'one_time') {
      mode = 'subscription';
      const matchingPlan = Array.from(section.querySelectorAll('[data-bn-cyp-plan]')).find(
        (btn) => String(btn.dataset.sellingPlanId) === String(sellingPlanId)
      );
      if (matchingPlan) {
        button = matchingPlan;
      }
    }

    const sticky = readStickyDataFromButton(button);
    const variantId = variantInput?.value || button?.dataset.variantId || '';
    const variant = product?.variants?.find((v) => String(v.id) === String(variantId));

    let imageUrl = '';
    if (variant?.featured_image?.src) {
      imageUrl = variant.featured_image.src;
    } else if (product?.featured_image) {
      imageUrl = product.featured_image;
    }

    return {
      section,
      mode,
      variantId,
      sellingPlanId,
      available: cta ? !cta.disabled : true,
      quantity: quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1,
      productTitle: product?.title || '',
      imageUrl,
      ...sticky,
    };
  }

  function emitPlanChange(section) {
    if (!section) return;
    const detail = getPlanChangeDetail(section);
    section.dispatchEvent(new CustomEvent('bn:plan-change', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('bn:plan-change', { detail }));
  }

  function publishCartUpdate(variantId, cartData) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId: variantId,
        cartData,
      });
    }
  }

  function initSection(section) {
    if (!section || section.dataset.bnCypInit === 'true') return;
    section.dataset.bnCypInit = 'true';

    const form = section.querySelector('.bn-cyp__form');
    const variantInput = section.querySelector('[data-bn-cyp-variant-id]');
    const sellingPlanInput = section.querySelector('[data-bn-cyp-selling-plan]');
    const cta = section.querySelector('[data-bn-cyp-cta]');
    const ctaLabel = section.querySelector('[data-bn-cyp-cta-label]');
    const errorEl = section.querySelector('[data-bn-cyp-error]');
    const planButtons = section.querySelectorAll('[data-bn-cyp-plan]');
    const oneTimeBtn = section.querySelector('[data-bn-cyp-one-time]');
    const gifts = section.querySelectorAll('[data-bn-cyp-gift]');
    const optionSelects = section.querySelectorAll('[data-bn-pd-option]');
    const skuEl = section.querySelector('[data-bn-pd-sku-value]');

    const soldOutLabel = section.dataset.bnCypSoldOutLabel || 'Sold out';
    const product = getProduct(section);
    const syncingSellingPlan = { value: false };

    const gallery = section.querySelector('[data-bn-cyp-gallery]');
    const slides = section.querySelectorAll('[data-bn-cyp-slide]');
    const dots = section.querySelectorAll('[data-bn-cyp-dot]');
    const thumbs = section.querySelectorAll('[data-bn-cyp-thumb]');

    let activeSlide = 0;
    let touchStartX = 0;
    let touchStartY = 0;
    let selectedMode = 'subscription';

    function showSlide(index) {
      if (!slides.length) return;
      activeSlide = Math.max(0, Math.min(index, slides.length - 1));
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === activeSlide);
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === activeSlide);
        dot.setAttribute('aria-selected', i === activeSlide ? 'true' : 'false');
      });
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === activeSlide);
      });
    }

    function showVariantMedia(variant) {
      if (!variant?.featured_media?.id || !slides.length) return;
      const mediaId = String(variant.featured_media.id);
      slides.forEach((slide, i) => {
        if (String(slide.dataset.mediaId) === mediaId) {
          showSlide(i);
        }
      });
    }

    if (gallery && slides.length > 1) {
      gallery.addEventListener(
        'touchstart',
        (e) => {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
        },
        { passive: true }
      );

      gallery.addEventListener(
        'touchend',
        (e) => {
          const diffX = touchStartX - e.changedTouches[0].clientX;
          const diffY = Math.abs(touchStartY - e.changedTouches[0].clientY);
          if (Math.abs(diffX) > 40 && diffY < 80) {
            if (diffX > 0 && activeSlide < slides.length - 1) showSlide(activeSlide + 1);
            if (diffX < 0 && activeSlide > 0) showSlide(activeSlide - 1);
          }
        },
        { passive: true }
      );

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.bnCypDot, 10);
          if (!Number.isNaN(idx)) showSlide(idx);
        });
      });

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const idx = parseInt(thumb.dataset.bnCypThumb, 10);
          if (!Number.isNaN(idx)) showSlide(idx);
        });
      });
    }

    function updateGifts(planType, planKey) {
      gifts.forEach((gift) => {
        const unlock = gift.dataset.bnCypGiftUnlock || 'subscription';
        let unlocked = false;
        if (unlock === 'subscription') unlocked = planType === 'subscription';
        if (unlock === 'multi') {
          unlocked =
            planType === 'subscription' &&
            (planKey === 'subscribe-3' || planKey === 'subscribe-2');
        }
        gift.classList.toggle('is-unlocked', unlocked);
        const lock = gift.querySelector('[data-bn-cyp-gift-lock]');
        if (lock) lock.hidden = unlocked;
        const status = gift.querySelector('[data-bn-cyp-gift-status]');
        if (status) {
          if (unlock === 'multi') {
            status.textContent = unlocked
              ? status.dataset.unlocked || 'ENTERED'
              : status.dataset.locked || 'LOCKED';
          } else {
            status.textContent = status.dataset.unlocked || 'FREE';
          }
        }
      });
    }

    function setCtaAvailability(available) {
      if (!cta) return;
      cta.disabled = !available;
      if (ctaLabel && !available) {
        ctaLabel.textContent = soldOutLabel;
      }
    }

    function applyVariantToPlans(variant) {
      if (!variant) return;

      planButtons.forEach((btn) => {
        if (btn.dataset.planVariantLocked === 'true') return;
        btn.dataset.variantId = String(variant.id);
        hydratePlanButton(btn, product);
        const hasSellingPlan = Boolean(btn.dataset.sellingPlanId);
        const available =
          Boolean(variant.available) &&
          hasSellingPlan &&
          isPlanAvailableForVariant(variant, btn.dataset.sellingPlanId);
        btn.dataset.available = available ? 'true' : 'false';
        btn.disabled = !available;
        if (available) updatePlanButtonStickyData(btn, variant);
      });

      if (oneTimeBtn) {
        oneTimeBtn.dataset.variantId = String(variant.id);
        const oneTimeLabel = oneTimeBtn.textContent.split(/\s+\$/)[0] || oneTimeBtn.dataset.oneTimePrefix || 'One Time Purchase';
        oneTimeBtn.dataset.oneTimePrefix = oneTimeLabel;
        oneTimeBtn.textContent = `${oneTimeLabel} ${formatMoney(variant.price)}`;
        oneTimeBtn.dataset.stickyPerMonth = formatMoney(variant.price);
        if (variant.compare_at_price > variant.price) {
          oneTimeBtn.dataset.stickyCompareAt = formatMoney(variant.compare_at_price);
          const savePct = Math.round(
            ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price
          );
          oneTimeBtn.dataset.stickyDiscount = `${savePct}% OFF`;
        } else {
          oneTimeBtn.dataset.stickyCompareAt = '';
          oneTimeBtn.dataset.stickyDiscount = '';
        }
      }

      if (skuEl) {
        skuEl.textContent = variant.sku || '';
      }

      showVariantMedia(variant);
      updateVariantUrl(section, variant.id);
    }

    function getCurrentSelection() {
      if (oneTimeBtn?.classList.contains('is-selected')) {
        return { type: 'one_time', button: oneTimeBtn };
      }
      const selectedPlan = section.querySelector('[data-bn-cyp-plan].is-selected');
      if (selectedPlan) {
        return { type: 'subscription', button: selectedPlan };
      }
      return { type: null, button: null };
    }

    function selectPlan(button) {
      if (!button || button.disabled) return;

      planButtons.forEach((btn) => {
        const selected = btn === button;
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-checked', selected ? 'true' : 'false');
        const perks = btn.querySelector('.bn-cyp__plan-perks');
        if (perks) perks.setAttribute('aria-hidden', selected ? 'false' : 'true');
      });

      if (oneTimeBtn) oneTimeBtn.classList.remove('is-selected');

      const variantId = button.dataset.variantId;
      const sellingPlanId = button.dataset.sellingPlanId || '';
      const planType = button.dataset.planType || 'subscription';
      const planKey = button.dataset.bnCypPlan || '';
      const available = button.dataset.available !== 'false';

      selectedMode = planType;

      if (variantInput) variantInput.value = variantId || '';
      setSellingPlanOnForm(
        section,
        planType === 'one_time' ? '' : sellingPlanId,
        planType === 'one_time',
        syncingSellingPlan
      );

      if (ctaLabel && available) {
        updateCtaLabel(section, button, planType === 'one_time' ? 'one_time' : 'subscription');
      }

      setCtaAvailability(available);
      updateGifts(planType, planKey);
      syncFormInputs(variantInput, sellingPlanInput);

      if (product && variantId) {
        const variant = product.variants?.find((v) => String(v.id) === String(variantId));
        if (variant) showVariantMedia(variant);
        updateVariantUrl(section, variantId);
      }

      emitPlanChange(section);
    }

    function selectOneTime() {
      planButtons.forEach((btn) => {
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-checked', 'false');
        const perks = btn.querySelector('.bn-cyp__plan-perks');
        if (perks) perks.setAttribute('aria-hidden', 'true');
      });

      if (oneTimeBtn) oneTimeBtn.classList.add('is-selected');

      const variantId = oneTimeBtn?.dataset.variantId || section.dataset.bnCypOneTimeVariant || '';
      const variant = product?.variants?.find((v) => String(v.id) === String(variantId));
      const available = variant ? Boolean(variant.available) : true;

      selectedMode = 'one_time';

      if (variantInput) variantInput.value = variantId;
      setSellingPlanOnForm(section, '', true, syncingSellingPlan);

      if (ctaLabel && oneTimeBtn && available) {
        updateCtaLabel(section, oneTimeBtn, 'one_time');
      }

      setCtaAvailability(available);
      updateGifts('one_time', 'one-time');
      syncFormInputs(variantInput, sellingPlanInput);

      if (variant) {
        showVariantMedia(variant);
        updateVariantUrl(section, variant.id);
      }

      emitPlanChange(section);
    }

    function handleVariantOptionsChange() {
      if (!product) return;

      const options = getSelectedOptions(section);
      const variant = findVariant(product, options);
      if (!variant) return;

      applyVariantToPlans(variant);

      const current = getCurrentSelection();
      if (current.type === 'one_time') {
        selectOneTime();
      } else if (current.button) {
        selectPlan(current.button);
      } else if (variantInput) {
        variantInput.value = String(variant.id);
        setCtaAvailability(Boolean(variant.available));
        syncFormInputs(variantInput, sellingPlanInput);
      }

      emitPlanChange(section);
    }

    planButtons.forEach((button) => {
      button.addEventListener('click', () => selectPlan(button));
    });

    if (oneTimeBtn) {
      oneTimeBtn.addEventListener('click', selectOneTime);
    }

    optionSelects.forEach((select) => {
      select.addEventListener('change', handleVariantOptionsChange);
    });

    gifts.forEach((gift) => {
      gift.addEventListener('click', () => {
        const targetPlan = gift.dataset.bnCypGiftTarget;
        if (targetPlan === 'one-time') {
          selectOneTime();
          return;
        }
        const planBtn = section.querySelector(`[data-bn-cyp-plan="${targetPlan}"]`);
        if (planBtn && !planBtn.disabled) selectPlan(planBtn);
      });
    });

    if (product) {
      planButtons.forEach((btn) => {
        hydratePlanButton(btn, product);
      });
    }

    const defaultPlan =
      section.querySelector('[data-bn-cyp-plan].is-selected:not([disabled])') ||
      Array.from(planButtons).find((btn) => btn.dataset.available !== 'false' && !btn.disabled) ||
      section.querySelector('[data-bn-cyp-plan].is-selected') ||
      planButtons[0];

    if (defaultPlan) {
      if (defaultPlan.disabled && defaultPlan.dataset.sellingPlanId) {
        defaultPlan.disabled = false;
        defaultPlan.dataset.available = 'true';
      }
      selectPlan(defaultPlan);
    } else if (oneTimeBtn) {
      selectOneTime();
    } else if (variantInput?.value && sellingPlanInput?.value) {
      syncFormInputs(variantInput, sellingPlanInput);
      emitPlanChange(section);
    }

    const quantityInput = section.querySelector('.bn-cyp__form [name="quantity"]');
    if (quantityInput) {
      quantityInput.addEventListener('change', () => emitPlanChange(section));
      quantityInput.addEventListener('input', () => emitPlanChange(section));
    }

    if (sellingPlanInput) {
      sellingPlanInput.addEventListener('input', () => emitPlanChange(section));
      sellingPlanInput.addEventListener('change', () => emitPlanChange(section));
      new MutationObserver(() => emitPlanChange(section)).observe(sellingPlanInput, {
        attributes: true,
        attributeFilter: ['disabled', 'value'],
      });
    }

    if (variantInput) {
      variantInput.addEventListener('change', () => emitPlanChange(section));
    }

    if (form) {
      form.addEventListener('change', (e) => {
        if (syncingSellingPlan.value) return;

        const name = e.target?.name;
        if (name === 'selling_plan' && !e.target.hasAttribute('data-bn-cyp-selling-plan')) {
          const planId = e.target.value;
          if (!planId) {
            selectOneTime();
            return;
          }
          const matchingBtn = Array.from(planButtons).find(
            (btn) => String(btn.dataset.sellingPlanId) === String(planId)
          );
          if (matchingBtn && !matchingBtn.disabled) {
            selectPlan(matchingBtn);
          } else {
            emitPlanChange(section);
          }
          return;
        }

        if (name === 'selling_plan' || name === 'id' || name === 'quantity') {
          emitPlanChange(section);
        }
      });

      const appWrapper = form.querySelector('[data-bn-pd-subscription-app]');
      if (appWrapper) {
        const syncAppWidget = () => {
          setSellingPlanOnForm(
            section,
            sellingPlanInput?.disabled ? '' : sellingPlanInput?.value || '',
            Boolean(oneTimeBtn?.classList.contains('is-selected')),
            syncingSellingPlan
          );
        };

        setTimeout(syncAppWidget, 0);
        setTimeout(syncAppWidget, 500);
        setTimeout(syncAppWidget, 1500);

        const appObserver = new MutationObserver(() => {
          if (syncingSellingPlan.value) return;
          const checked = appWrapper.querySelector('input[name="selling_plan"]:checked');
          if (!checked?.value) return;
          const matchingBtn = Array.from(planButtons).find(
            (btn) => String(btn.dataset.sellingPlanId) === String(checked.value)
          );
          if (matchingBtn && !matchingBtn.classList.contains('is-selected')) {
            selectPlan(matchingBtn);
          }
        });
        appObserver.observe(appWrapper, { childList: true, subtree: true, attributes: true });
      }
    }

    if (form && cta) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (cta.disabled) return;

        if (errorEl) errorEl.hidden = true;
        cta.disabled = true;

        const formData = new FormData(form);
        const variantId = formData.get('id');

        if (!variantId) {
          if (errorEl) {
            errorEl.textContent = section.dataset.bnCypErrorNoVariant || 'Please select a plan.';
            errorEl.hidden = false;
          }
          cta.disabled = false;
          return;
        }

        const sellingPlan = formData.get('selling_plan');
        const isOneTime = Boolean(oneTimeBtn?.classList.contains('is-selected'));

        if (!isOneTime && !sellingPlan) {
          if (errorEl) {
            errorEl.textContent =
              section.dataset.bnCypErrorNoSellingPlan ||
              'This subscription plan is not available. Please choose another option.';
            errorEl.hidden = false;
          }
          cta.disabled = false;
          return;
        }

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' } };

        if (config.headers) {
          delete config.headers['Content-Type'];
        }

        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((s) => s.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(document.activeElement);
          }
        }

        config.body = formData;

        const addUrl = window.routes?.cart_add_url || '/cart/add';

        try {
          const response = await fetch(addUrl, config);
          const data = await response.json();

          if (data.status) {
            if (errorEl) {
              errorEl.textContent = data.description || data.message || 'Unable to add to cart.';
              errorEl.hidden = false;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: data.errors || data.description,
                message: data.message,
              });
            }
            cta.disabled = false;
            return;
          }

          publishCartUpdate(variantId, data);

          if (cart && typeof cart.renderContents === 'function') {
            cart.classList.remove('is-empty');
            cart.renderContents(data);
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
          }
        } catch (err) {
          if (errorEl) {
            errorEl.textContent = section.dataset.bnCypErrorNetwork || 'Network error. Please try again.';
            errorEl.hidden = false;
          }
        } finally {
          const current = getCurrentSelection();
          if (current.type === 'one_time' && oneTimeBtn) {
            const variant = product?.variants?.find(
              (v) => String(v.id) === String(oneTimeBtn.dataset.variantId)
            );
            setCtaAvailability(variant ? Boolean(variant.available) : true);
          } else if (current.button) {
            setCtaAvailability(current.button.dataset.available !== 'false');
          } else {
            cta.disabled = false;
          }
        }
      });
    }

    const animates = section.querySelectorAll('.bn-cyp__animate, .bn-pd__animate');
    if (animates.length && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: section.dataset.bnCypObserverMargin || '-60px' }
      );
      animates.forEach((el) => observer.observe(el));
    } else {
      animates.forEach((el) => el.classList.add('is-visible'));
    }
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector(SECTION_SELECTOR) || e.target;
    if (section.matches && section.matches(SECTION_SELECTOR)) {
      section.dataset.bnCypInit = 'false';
      initSection(section);
    }
  });
})();
